import React, { useEffect, useState } from "react";
import { getTransferSubmissions, performTransfer } from "../../actions/studensts";
import { exportPendingTransfers, exportTransferredStudents } from "../exportToExcel";
import Loading from "../../components/Loading";

// ── Category display: B suffix shown while student is still in private ──────
const DISPLAY_CATEGORY = {
  "individual": "individualB",
  "foreign":    "foreignB",
  "outside":    "OutsideB",
};

// ── Category result after transfer (A suffix) ────────────────────────────────
const TRANSFER_CATEGORY = {
  "individual":   "individualA",
  "foreign":      "foreignA",
  "foreignB_UAE": "foreignA_UAE",
  "outside":      "OutsideA",
  // ForeignUSD stays unchanged
};

function displayCat(cat) { return cat ? (DISPLAY_CATEGORY[cat] || cat) : null; }
function transferCat(cat) { return cat ? (TRANSFER_CATEGORY[cat] || cat) : cat; }

const BADGE = {
  id:       { label: "Matched by ID",       cls: "bg-green-100 text-green-700 border border-green-300" },
  passport: { label: "Matched by Passport", cls: "bg-blue-100  text-blue-700  border border-blue-300"  },
  none:     { label: "Not Found",           cls: "bg-red-100   text-red-600   border border-red-300"   },
};

function MatchBadge({ matchedBy }) {
  const b = BADGE[matchedBy] || BADGE.none;
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${b.cls}`}>
      {b.label}
    </span>
  );
}

// ── Confirmation Modal ────────────────────────────────────────────────────────
function ConfirmModal({ row, onConfirm, onCancel, busy }) {
  const oldCat = displayCat(row.category) || "—";
  const newCat = transferCat(row.category) || oldCat;
  const oldUni = row.university || "—";
  const newUni = "Public";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-blue-900 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Confirm Transfer</h2>
          <p className="text-blue-200 text-sm mt-0.5">
            {row.full_name || row.national_id}
            {row.full_name && <span className="opacity-70"> · {row.national_id}</span>}
          </p>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">
            The following changes will be applied to the student record:
          </p>
          <table className="w-full text-sm mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                <th className="px-4 py-2 text-left">Field</th>
                <th className="px-4 py-2 text-left">Before</th>
                <th className="px-4 py-2 text-left">After</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-2 font-medium text-gray-700">Category</td>
                <td className="px-4 py-2 text-red-500">{oldCat}</td>
                <td className="px-4 py-2 text-green-600 font-semibold">{newCat}</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-medium text-gray-700">University</td>
                <td className="px-4 py-2 text-red-500">{oldUni}</td>
                <td className="px-4 py-2 text-green-600 font-semibold">{newUni}</td>
              </tr>
            </tbody>
          </table>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-5 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm
                hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className="px-5 py-2 rounded-lg text-white text-sm font-semibold
                bg-gradient-to-r from-blue-700 to-blue-500
                hover:from-blue-800 hover:to-blue-600
                transition shadow disabled:opacity-50 active:scale-95"
            >
              {busy ? "Transferring…" : "✓ Confirm Transfer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TransferSubmissions() {
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [filter, setFilter]             = useState("all");
  const [confirmModal, setConfirmModal] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [error, setError]               = useState(null);

  // Permission check: admin role OR specific usernames
  const profile  = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const canExport =
    profile?.role === "admin" ||
    profile?.username === "asmaa" ||
    profile?.username === "abdelrhman";

  useEffect(() => {
    (async () => {
      const data = await getTransferSubmissions();
      setRows(data || []);
      setLoading(false);
    })();
  }, []);

  const pending     = rows.filter((r) => !r.is_transferred);
  const transferred = rows.filter((r) =>  r.is_transferred);

  const filteredPending = pending.filter((r) => {
    const q = search.toLowerCase();
    const matchesSearch =
      (r.national_id   || "").toLowerCase().includes(q) ||
      (r.full_name     || "").toLowerCase().includes(q) ||
      (r.student_phone || "").includes(q) ||
      (r.category      || "").toLowerCase().includes(q) ||
      (r.university    || "").toLowerCase().includes(q);
    const matchesFilter =
      filter === "all"       ? true :
      filter === "matched"   ? !!r.matched_by :
      filter === "unmatched" ? !r.matched_by : true;
    return matchesSearch && matchesFilter;
  });

  const matched   = pending.filter((r) => !!r.matched_by).length;
  const unmatched = pending.length - matched;

  const fmtDate = (val) => {
    if (!val) return "—";
    return new Date(val).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const handleTransferClick = (row) => { setError(null); setConfirmModal({ row }); };

  const handleConfirm = async () => {
    if (!confirmModal) return;
    const { row } = confirmModal;
    setTransferring(true);
    const result = await performTransfer(row.id, row.national_id);
    setTransferring(false);
    if (result?.status === 200) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                is_transferred:  1,
                transferred_at:  new Date().toISOString(),
                transferred_by:  result.data?.transferred_by ?? null,
                category:        result.data?.new_category   ?? r.category,
                university:      result.data?.new_university  ?? r.university,
              }
            : r
        )
      );
      setConfirmModal(null);
    } else {
      setError("Transfer failed. Please try again.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50">

      {confirmModal && (
        <ConfirmModal
          row={confirmModal.row}
          onConfirm={handleConfirm}
          onCancel={() => !transferring && setConfirmModal(null)}
          busy={transferring}
        />
      )}

      {/* Header banner */}
      <div className="bg-blue-900 px-6 py-6 mb-6">
        <div className="max-w-screen-xl mx-auto">
          <h1 className="text-2xl font-bold text-white">Transfer Submissions</h1>
          <p className="text-sm text-blue-200 mt-0.5">Private → Public University Transfer Requests</p>
        </div>
      </div>

      <div className="px-6 max-w-screen-xl mx-auto">

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-300 text-red-600 rounded-lg text-sm flex items-center gap-2">
            <span>⚠</span> {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Submissions", value: rows.length,        color: "bg-blue-900"   },
            { label: "Matched",           value: matched,            color: "bg-green-600"  },
            { label: "Not Found",         value: unmatched,          color: "bg-red-500"    },
            { label: "Transferred",       value: transferred.length, color: "bg-purple-700" },
          ].map(({ label, value, color }) => (
            <div key={label} className={`${color} text-white rounded-xl p-4 text-center shadow`}>
              <p className="text-3xl font-bold">{value}</p>
              <p className="text-sm opacity-80 mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* ══ SECTION 1: PENDING TRANSFERS ══ */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-400"></span>
            Pending Transfers
            <span className="ml-1 text-sm font-normal text-gray-400">({pending.length})</span>
            {canExport && pending.length > 0 && (
              <button
                onClick={() => exportPendingTransfers(pending)}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                  bg-green-600 hover:bg-green-700 text-white transition shadow active:scale-95"
              >
                ⬇ Export Excel
              </button>
            )}
          </h2>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="text"
              placeholder="Search by ID, name, phone, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-48 border border-gray-300 rounded-lg px-4 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {["all", "matched", "unmatched"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition
                  ${filter === f ? "bg-blue-900 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {f === "all"     ? `All (${pending.length})` :
                 f === "matched" ? `Matched (${matched})` :
                                  `Not Found (${unmatched})`}
              </button>
            ))}
          </div>

          {filteredPending.length === 0 ? (
            <div className="text-center py-14 text-gray-400 border border-dashed border-gray-200 rounded-xl">
              {pending.length === 0 ? "No pending submissions — all have been transferred." : "No submissions match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl shadow border border-gray-200">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="bg-blue-900 text-white text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 text-left">#</th>
                    <th className="px-4 py-3 text-left">National ID / Passport</th>
                    <th className="px-4 py-3 text-left">Student Phone</th>
                    <th className="px-4 py-3 text-left">Father Phone</th>
                    <th className="px-4 py-3 text-left">Mother Phone</th>
                    <th className="px-4 py-3 text-left">Full Name</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-left">University</th>
                    <th className="px-4 py-3 text-left">Match</th>
                    <th className="px-4 py-3 text-left">Submitted At</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPending.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={`transition hover:bg-yellow-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                    >
                      <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-mono text-gray-800">{row.national_id || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.student_phone}</td>
                      <td className="px-4 py-3 text-gray-700">{row.father_phone}</td>
                      <td className="px-4 py-3 text-gray-700">{row.mother_phone}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {row.full_name || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {row.category
                          ? <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
                              {displayCat(row.category)}
                            </span>
                          : <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.university || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3"><MatchBadge matchedBy={row.matched_by} /></td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(row.submitted_at)}</td>
                      <td className="px-4 py-3 text-center">
                        {row.matched_by ? (
                          <button
                            onClick={() => handleTransferClick(row)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white
                              bg-gradient-to-r from-blue-700 to-blue-500
                              hover:from-blue-800 hover:to-blue-600
                              transition shadow active:scale-95 whitespace-nowrap"
                          >
                            ⇄ Transfer
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs italic">No match</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ══ SECTION 2: TRANSFERRED STUDENTS ══ */}
        <div className="mb-10">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
            Transferred Students
            <span className="ml-1 text-sm font-normal text-gray-400">({transferred.length})</span>
            {canExport && transferred.length > 0 && (
              <button
                onClick={() => exportTransferredStudents(transferred)}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                  bg-green-700 hover:bg-green-800 text-white transition shadow active:scale-95"
              >
                ⬇ Export Excel
              </button>
            )}
          </h2>

          {transferred.length === 0 ? (
            <div className="text-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-xl">
              No students have been transferred yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl shadow border border-gray-200">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="bg-green-700 text-white text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 text-left">#</th>
                    <th className="px-4 py-3 text-left">National ID / Passport</th>
                    <th className="px-4 py-3 text-left">Full Name</th>
                    <th className="px-4 py-3 text-left">New Category</th>
                    <th className="px-4 py-3 text-left">University</th>
                    <th className="px-4 py-3 text-left">Submitted At</th>
                    <th className="px-4 py-3 text-left">Transferred At</th>
                    <th className="px-4 py-3 text-left">Transferred By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transferred.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={`${idx % 2 === 0 ? "bg-white" : "bg-green-50"}`}
                    >
                      <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-mono text-gray-800">{row.national_id || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {row.full_name || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium">
                          {row.category || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-green-700">{row.university || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(row.submitted_at)}</td>
                      <td className="px-4 py-3 text-green-600 whitespace-nowrap font-medium">{fmtDate(row.transferred_at)}</td>
                      <td className="px-4 py-3">
                        {row.transferred_by
                          ? <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold">{row.transferred_by}</span>
                          : <span className="text-gray-300 italic text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
